import { useEffect, RefObject } from 'react'

interface UseScrollbarSyncOptions {
  topScrollRef: RefObject<HTMLDivElement>
  contentScrollRef: RefObject<HTMLDivElement>
  contentRef: RefObject<HTMLDivElement>
  vehicleHeaderScrollRef?: RefObject<HTMLDivElement>
  contentKey?: string
}

export function useScrollbarSync({
  topScrollRef,
  contentScrollRef,
  contentRef,
  vehicleHeaderScrollRef,
  contentKey,
}: UseScrollbarSyncOptions) {
  useEffect(() => {
    const topScroll = topScrollRef.current
    const contentScroll = contentScrollRef.current
    const content = contentRef.current
    const vehicleHeaderScroll = vehicleHeaderScrollRef?.current

    if (!topScroll || !contentScroll || !content) return

    const handleTopScroll = () => {
      if (contentScroll.scrollLeft !== topScroll.scrollLeft) {
        contentScroll.scrollLeft = topScroll.scrollLeft
      }
      if (vehicleHeaderScroll && vehicleHeaderScroll.scrollLeft !== topScroll.scrollLeft) {
        vehicleHeaderScroll.scrollLeft = topScroll.scrollLeft
      }
    }

    const handleContentScroll = () => {
      if (topScroll.scrollLeft !== contentScroll.scrollLeft) {
        topScroll.scrollLeft = contentScroll.scrollLeft
      }
      if (vehicleHeaderScroll && vehicleHeaderScroll.scrollLeft !== contentScroll.scrollLeft) {
        vehicleHeaderScroll.scrollLeft = contentScroll.scrollLeft
      }
    }

    topScroll.addEventListener('scroll', handleTopScroll)
    contentScroll.addEventListener('scroll', handleContentScroll)

    // Set initial width for top scrollbar and vehicle header
    const updateScrollbarWidth = () => {
      const scrollWidth = content.scrollWidth
      const topScrollContent = topScroll.querySelector<HTMLDivElement>(':first-child')
      if (topScrollContent) {
        topScrollContent.style.width = `${scrollWidth}px`
      }
      if (vehicleHeaderScroll) {
        const vehicleHeaderContent = vehicleHeaderScroll.querySelector<HTMLDivElement>('#vehicle-header-content')
        if (vehicleHeaderContent) {
          vehicleHeaderContent.style.width = `${scrollWidth}px`
        }
      }
    }

    // Initial update
    updateScrollbarWidth()

    // Create a ResizeObserver to watch for content size changes
    const resizeObserver = new ResizeObserver(() => {
      updateScrollbarWidth()
    })
    resizeObserver.observe(content)

    return () => {
      topScroll.removeEventListener('scroll', handleTopScroll)
      contentScroll.removeEventListener('scroll', handleContentScroll)
      resizeObserver.disconnect()
    }
  }, [topScrollRef, contentScrollRef, contentRef, vehicleHeaderScrollRef, contentKey])
}
